import { useClientPortalSelector, useClientPortalDispatch, selectBooking, goToStep } from "../../../redux";
import {
    StepId,
    STEP_LOCATION,
    STEP_SERVICE,
    STEP_PROVIDER,
    STEP_AVAILABILITY,
    STEP_REGISTER,
    STEP_CONFIRMATION,
    IBookingState,
} from "../bookingSlice.types";
import { useEffectiveStepOrder } from "./useEffectiveStepOrder";
import { useCallback } from "react";

interface IUseBookingNavigationHook {
    currentStep: StepId;
    effectiveCurrentStep: StepId;
    effectiveStepOrder: StepId[];
    canGoPrevious: boolean;
    canGoNext: boolean;
    handleNext: () => void;
    handlePrevious: () => void;
    handleGoToStep: (step: StepId) => void;
}

const isStepCompleted = (booking: IBookingState, step: StepId): boolean => {
    switch (step) {
        case STEP_LOCATION:
            return booking.location.isCompleted;
        case STEP_SERVICE:
            return booking.service.isCompleted;
        case STEP_PROVIDER:
            return booking.provider.isCompleted;
        case STEP_AVAILABILITY:
            return booking.availability.isCompleted;
        case STEP_REGISTER:
            return booking.registration.isCompleted;
        case STEP_CONFIRMATION:
            return true;
        default:
            return false;
    }
};

/**
 * Hook that provides navigation functionality for the booking flow.
 * Derives step order from booking type and login state.
 * Derives canGoNext/canGoPrevious from step completion flags.
 */
export function useBookingNavigation(): IUseBookingNavigationHook {
    const dispatch = useClientPortalDispatch();
    const booking = useClientPortalSelector(selectBooking);
    const { effectiveStepOrder, currentStepIndex, effectiveCurrentStep } = useEffectiveStepOrder();

    const currentStep = booking?.navigation?.currentStep ?? STEP_LOCATION;

    const canGoPrevious = currentStepIndex > 0;
    const isCurrentStepCompleted = isStepCompleted(booking, effectiveCurrentStep);
    const canGoNext =
        currentStepIndex >= 0 && currentStepIndex < effectiveStepOrder.length - 1 && isCurrentStepCompleted;

    const handleNext = useCallback(() => {
        if (currentStepIndex >= 0 && currentStepIndex < effectiveStepOrder.length - 1) {
            const nextStep = effectiveStepOrder[currentStepIndex + 1];
            dispatch(goToStep(nextStep));
        }
    }, [currentStepIndex, effectiveStepOrder, dispatch]);

    const handlePrevious = useCallback(() => {
        if (canGoPrevious) {
            const previousStep = effectiveStepOrder[currentStepIndex - 1];
            dispatch(goToStep(previousStep));
        }
    }, [canGoPrevious, currentStepIndex, effectiveStepOrder, dispatch]);

    const handleGoToStep = useCallback(
        (step: StepId) => {
            if (effectiveStepOrder.includes(step)) {
                dispatch(goToStep(step));
            }
        },
        [dispatch, effectiveStepOrder]
    );

    return {
        currentStep,
        effectiveCurrentStep,
        effectiveStepOrder,
        canGoPrevious,
        canGoNext,
        handleNext,
        handlePrevious,
        handleGoToStep,
    };
}
